import React, { useState, useRef, useEffect } from 'react'
import NewCarousel from '/react4xp/components/NewCarousel'
import Video from '/react4xp/components/Video'
import { ExpertiseType } from './type'

// svg
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'

export default function Expertise(props: ExpertiseType) {
   const { title, intro, video, images, firstColumn, secondColumn } = props

   const [isOpen, setOpen] = useState<boolean>(false)

   const ref = useRef<HTMLDivElement>(null)
   const [contentHeight, setContentHeight] = useState<number>(0)

   const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992)

   const calculateMobile = () => {
      setIsMobile(window.innerWidth < 768)
   }

   const carousel = (images || []).map(image => ({
      image: {
         src: image.src ?? '',
         srcMobile: image.srcMobile ?? '',
      },
      imageAlt: image.alt ? image.alt : 'Image',
      description: image.caption ? image.caption : '',
   }))

   const formattedVideoProps = video ? {
      url: video.src ?? '',
      videoType: video.type ?? "default",
      poster: images?.[0]?.src ?? '',
      posterMedium: images?.[0]?.srcMobile ?? '',
   } : null

   const closeExpertise = () => {
      if (ref.current) {
         const videos = ref.current.querySelectorAll(video && video.type === 'default' ? 'video' : 'iframe')

         videos.forEach(video => {
            if (video instanceof HTMLVideoElement) {
               video.pause()
            } else if (video instanceof HTMLIFrameElement) {
               if (video.src.includes('youtube.com') || video.src.includes('youtu.be')) {
                  video.contentWindow?.postMessage(
                     JSON.stringify({ event: 'command', func: 'pauseVideo' }),
                     '*'
                  )
               } else if (video.src.includes('vimeo.com')) {
                  video.contentWindow?.postMessage(
                     JSON.stringify({ method: 'pause' }),
                     '*'
                  )
               }
            }
         })
      }

      setOpen(false)
      setContentHeight(0)
   }

   const openExpertise = () => {
      setOpen(true)
      setContentHeight(ref.current?.getBoundingClientRect().height || 0)
   }

   const toggleHandler = () => {
      isOpen ? closeExpertise() : openExpertise()
   }

   const handleResize = () => {
      if (isOpen) {
         setContentHeight(ref.current?.getBoundingClientRect().height || 0)
      }

      calculateMobile()
   }

   useEffect(() => {
      window.addEventListener('resize', handleResize)

      return () => {
         window.removeEventListener('resize', handleResize)
      }
   }, [contentHeight])

   return (
      <div className='expertise__container'>
         <div className="grid-container">
            <div className="grid-row">
               <div className="c-12">
                  <div className="expertise__separator"></div>
               </div>
            </div>
         </div>
         <div className={`expertise ${isOpen ? 'expertise--open' : ''}`}>
            <div className="grid-container">
               <div onClick={toggleHandler} className="expertise__toggler">
                  <div className="grid-row">
                     <div className="c-12">

                        <span>
                           {title ? title : 'Area of Expertise'}
                        </span>

                        <AngleDown />

                     </div>
                  </div>
               </div>
            </div>
            <div className="expertise__wrapper" style={{ maxHeight: `${contentHeight}px` }}>
               <div ref={ref} className="expertise__content">

                  {intro &&
                     <div className="grid-container">
                        <div className="grid-row">
                           <div className="c-md-10 start-md-2">
                              <div className="expertise__intro">
                                 <div dangerouslySetInnerHTML={{ __html: intro }} suppressHydrationWarning></div>
                              </div>
                           </div>
                        </div>
                     </div>
                  }

                  {video &&
                     <div className="grid-container">
                        <div className="grid-row">
                           <div className="c-md-10 start-md-2">
                              <div className="expertise__video">
                                 <Video video={formattedVideoProps} />
                              </div>
                           </div>
                        </div>
                     </div>
                  }


                  {(images && !video) &&
                     (carousel.length === 1 ?
                        <div className="grid-container">
                           <div className="grid-row">
                              <div className="c-md-10 start-md-2">
                                 <figure className="expertise__image">
                                    <picture>
                                       <source
                                          media="(min-width: 768px)"
                                          srcSet={images[0].src}
                                       />
                                       <source
                                          media="(max-width: 767px)"
                                          srcSet={images[0].srcMobile}
                                       />
                                       <img src={images[0].src} alt={images[0].alt ? images[0].alt : 'Image'} />
                                    </picture>
                                 </figure>
                              </div>
                           </div>
                        </div>
                        :
                        (images.length !== 0 && <div className="grid-container">
                           <div className="grid-row">
                              <div className="c-md-10 start-md-2">
                                 <div className="relative">
                                    <div className="relative">
                                       <NewCarousel
                                          images={images}
                                          contentColor='secondary'
                                          isMacro={true}
                                       />
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>)
                     )
                  }

                  {(images && !video) && (images.length === 1) && (images[0].caption) &&
                     <div className="grid-container">
                        <div className="grid-row expertise__caption">
                           <div className="c-md-8 start-md-3">
                              {images[0].caption ? (
                                 <div dangerouslySetInnerHTML={{ __html: images[0].caption }} suppressHydrationWarning></div>
                              ) : ''}
                           </div>
                        </div>
                     </div>
                  }

                  {(video && video.caption) &&
                     <div className="grid-container">
                        <div className="grid-row expertise__caption">
                           <div className="c-md-8 start-md-3">
                           {video.caption ? (
                                 <div dangerouslySetInnerHTML={{ __html: video.caption }} suppressHydrationWarning></div>
                              ) : ''}
                           </div>
                        </div>
                     </div>
                  }

                  {(firstColumn || secondColumn) &&
                     <div className="grid-container">
                        <div className="grid-row expertise__two-columns">
                           
                           {firstColumn && (
                              <div className="c-md-4 start-md-3">
                                 <div dangerouslySetInnerHTML={{ __html: firstColumn }} suppressHydrationWarning></div>
                              </div>
                           )}

                           {secondColumn && (
                              <div className="c-md-4">
                                 <div dangerouslySetInnerHTML={{ __html: secondColumn }} suppressHydrationWarning></div>
                              </div>
                           )}
                        </div>
                     </div>
                  }

               </div>
            </div>
         </div >
         <div className="grid-container">
            <div className="grid-row">
               <div className="c-12">
                  <div className="expertise__separator"></div>
               </div>
            </div>
         </div>
      </div>
   )
}
